import React, { useCallback, useRef, useState, type FC, useContext } from 'react';
import Header from '../../components/header/header';
import styles from './administrationPanel.module.scss';
import './administrationPanel.scss';
import DetailsViewProvider from '../../context/DetailsViewProvider';
import TabMenuAdmin from '../../components/tabMenuAdmin/tabMenuAdmin';
import UsersTable from './usersTable/usersTable';
import SimpleSelect from '../fullDetail/components/simpleSelect/simpleSelect';
import TitleValue from '../fullDetail/components/titleValue/titleValue';
import searchIcon from '../../assets/search.svg';
import debounce from 'lodash.debounce';
import ObrasAPI from '../../services/APIObras';
import ProjectAutocomplete from './components/projectAutocomplete/projectAutocomplete';
import { CenterParams, UsuarioProyecto } from '../../types/Admin';
import ProjectTable from './projectTable/projectTable';
import SearchUserDialog from './components/searchUserDialog/searchUserDialog';
import SearchRegisteredUserDialog from './components/searchRegisteredUserDialog/searchRegisteredUserDialog';
import EditRoles from '../editRoles/editRoles';
import {AdminContext} from '../../context/AdminProvider';
import LoadingSpinner from '../../components/loadingSpinner/loadingSpinner';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../services/authContextProvider';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface AdministrationPanelProps { }

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const AdministrationPanel: FC<AdministrationPanelProps> = () => {
  const api = ObrasAPI.getInstance();
  const [t] = useTranslation();
  const [userInput, setUserInput] = useState('');
  const [projectInput, setProjectInput] = useState('');

  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState<CenterParams | undefined>(undefined);
  const [usersPerProject, setUsersPerProject] = useState<UsuarioProyecto[]>([]);
  const adminContext = useContext(AdminContext);
  const searchMethod = adminContext.searchMethod;
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const projectSearchSequence = useRef(0);
  const projectSelectionSequence = useRef(0);
  const authContext = useContext(AuthContext);
  const canManageUserRoles = authContext.canSystem('can_manage_user_roles');

  const selectOptions = [
    { id: 0, nombre: t('administration.user') },
    { id: 1, nombre: t('administration.project') },
  ];

  // Queries the master-data API for work subtypes by code or name.
  const codeInputRequest = async (newValue: string) => {
    const sequence = ++projectSearchSequence.current;
    setProjectInput(newValue);
    if (newValue.length >= 3) {
      setLoadingAutocomplete(true);
      try {
        let results = await api.getProjectByCodeName(newValue);
        if (sequence !== projectSearchSequence.current) return;
        results = results.length ? results : [];
        setLoadingAutocomplete(false);
        return setAutocompleteOptions(results);
      } catch (error) {
        if (sequence === projectSearchSequence.current) setLoadingAutocomplete(false);
        console.log(error);
      }
    }
    setLoadingAutocomplete(false);
    return setAutocompleteOptions([]);
  };
  // Calls the master-data API each time that the autoselect input changes.
  const inputAutocompleteChangeHandler = useCallback(
    debounce(codeInputRequest, 250),
    [],
  );

  const handleChangeAutocomplete = async (project: CenterParams | undefined) => {
    const sequence = ++projectSelectionSequence.current;
    setLoading(true);
    try {
      setSelectedProject(project);
      if (project) {
        let results = await api.getUsersByProject(project);
        if (sequence !== projectSelectionSequence.current) return;
        results = results.length ? results : [];
        adminContext.handleChangeUsersPerProject(results);
        return setUsersPerProject(results);
      } else {
        adminContext.handleChangeUsersPerProject([]);
        return setUsersPerProject([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (sequence === projectSelectionSequence.current) setLoading(false);
    }
  };
  
  const [openDialog, setOpenDialog] = useState(false);
  const [openRegisteredUserDialog, setOpenRegisteredUserDialog] = useState(false);

  return (
    <DetailsViewProvider>
      {adminContext.isEdition ? 
        <EditRoles hasProject={adminContext.hasProject} />
        :
        <div data-testid="administration-test" className='administration'>
          <div>
            <Header>
              <TabMenuAdmin />
            </Header>
            <div className='adminPanel'>
              <div className='container'>
                <div className={styles.searchMethodContainer}>
                  <div className={styles.selectSearchMethod}>
                    <SimpleSelect
                      id='select-searchMethod'
                      options={selectOptions}
                      value={selectOptions[searchMethod]}
                      label={t('administration.searchBy')}
                      handleChangeValue={({id}) => {
                        adminContext.handleChangeSearchMethod(id);
                        setProjectInput('');
                        setSelectedProject(undefined);
                      }}
                      editable
                    />
                  </div>
                  {searchMethod === 0 &&
                    <>
                      <div className={styles.inputSearchMethod}>
                        <TitleValue
                          identifier='input-searchMethod'
                          title={t('administration.user')}
                          value={undefined}
                          handleChange={(value) => setUserInput(value)}
                          editable
                        />
                        <div className={styles.searchIcon}>
                          <img src={searchIcon} />
                        </div>
                      </div>
                      {canManageUserRoles && <div
                        className={styles.addUser}
                        onClick={() => setOpenRegisteredUserDialog(true)}
                      >
                        + {t('administration.addUser')}
                      </div>}
                    </>
                  }
                  {/* search by project*/}
                  {searchMethod === 1 &&
                    <>
                      <ProjectAutocomplete
                        options={autocompleteOptions}
                        onInputChange={inputAutocompleteChangeHandler}
                        onChange={handleChangeAutocomplete}
                        onClear={() => {
                          setProjectInput('');
                        }}
                        value={projectInput}
                        freeSolo={!projectInput || projectInput.length < 3 || loadingAutocomplete}
                      />
                      <div
                        className={selectedProject && canManageUserRoles ? styles.addUser : styles.addUserDisabled}
                        onClick={() => {
                          if (selectedProject && canManageUserRoles) {
                            setOpenDialog(true);
                            const projectToEdit = {
                              codEmpresa: '0008', 
                              codZona: selectedProject.codZona, 
                              codCentro: selectedProject.codCentro, 
                              codSupercentro: selectedProject.codSupercentro
                            };
                            adminContext.handleChangeProjectToAdd(projectToEdit);
                          }}}>
                        + {t('administration.addUser')}
                      </div>
                    </>
                  }
                </div>
                {searchMethod === 0 && <UsersTable userInput={userInput} canManageUserRoles={canManageUserRoles} />}
                {searchMethod === 1 &&
                <>
                  <ProjectTable
                    selectedProject={selectedProject}
                    canManageUserRoles={canManageUserRoles}
                  />
                  <SearchUserDialog
                    open={openDialog}
                    handleOpen={(b: boolean) => setOpenDialog(b)}
                    users={usersPerProject}
                    selectedProject={selectedProject!}
                  />
                </>}
                <SearchRegisteredUserDialog
                  open={openRegisteredUserDialog}
                  handleOpen={setOpenRegisteredUserDialog}
                />
                {loading && <LoadingSpinner />}
              </div>
            </div>
          </div>
        </div>
      }
    </DetailsViewProvider>
  );
};

export default AdministrationPanel;
